// src/Components/Common/LoadingSpinner.jsx
import React, { useState, useEffect } from 'react';

/**
 * Modern, minimalist loading spinner with subtle animations
 * @param {Object} props
 * @param {string} props.message - Optional message to display
 * @param {string} props.color - Color theme ('pink', 'blue', 'gradient')
 * @param {boolean} props.fullScreen - Whether to display full screen
 */
const LoadingSpinner = ({
  message = 'Loading...',
  color = 'gradient',
  fullScreen = true
}) => {
  const [progress, setProgress] = useState(0);
  const [dots, setDots] = useState(0);
  
  // Update animations
  useEffect(() => {
    // Progress animation
    const progressInterval = setInterval(() => {
      setProgress(prev => (prev < 100 ? prev + 0.5 : 0));
    }, 100);
    
    // Loading dots animation
    const dotsInterval = setInterval(() => {
      setDots(prev => (prev + 1) % 4);
    }, 500);
    
    return () => {
      clearInterval(progressInterval);
      clearInterval(dotsInterval);
    };
  }, []);
  
  // Color schemes
  const colors = {
    pink: {
      primary: "from-pink-500 to-pink-600",
      bg: "bg-pink-50"
    },
    blue: {
      primary: "from-blue-500 to-blue-600",
      bg: "bg-blue-50"
    },
    gradient: {
      primary: "from-pink-500 to-purple-600",
      bg: "bg-gradient-to-r from-pink-50 to-purple-50"
    }
  };
  
  // Get selected color scheme
  const theme = colors[color] || colors.gradient;
  
  // Loading dots display
  const getLoadingDots = () => '.'.repeat(dots);
  
  // Container component
  const Container = ({ children }) => {
    return fullScreen ? (
      <div className="fixed inset-0 flex items-center justify-center z-50 bg-white bg-opacity-95 backdrop-blur-sm">
        {children}
      </div>
    ) : (
      <div className="w-full flex items-center justify-center py-6">
        {children}
      </div>
    );
  };
  
  return (
    <Container>
      <div className="w-full max-w-sm mx-auto px-6 py-8 relative">
        {/* Logo */}
        <div className="text-center mb-6">
          <div className={`w-12 h-12 rounded-xl bg-gradient-to-r ${theme.primary} p-3 shadow-lg mx-auto`}>
            <svg className="w-full h-full text-white" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 17C14.2091 17 16 15.2091 16 13C16 10.7909 14.2091 9 12 9C9.79086 9 8 10.7909 8 13C8 15.2091 9.79086 17 12 17Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M12 1V4M12 20V23M4.22 4.22L6.34 6.34M17.66 17.66L19.78 19.78M1 12H4M20 12H23M4.22 19.78L6.34 17.66M17.66 6.34L19.78 4.22" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <p className="text-gray-600 text-sm font-medium mt-3">
            {message}
          </p>
        </div>
        
        {/* Progress Bar */}
        <div className="mb-6">
          <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
            <div 
              className={`h-full bg-gradient-to-r ${theme.primary} rounded-full relative overflow-hidden`}
              style={{ width: `${progress}%` }}
            >
              <div className="absolute inset-0 w-full h-full animate-shimmer">
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-30 transform -translate-x-full"></div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Status Indicator */}
        <div className="text-center">
          <div className={`inline-flex items-center justify-center px-3 py-1.5 rounded-full bg-gradient-to-r ${theme.primary} text-white text-xs font-medium shadow-sm`}>
            <svg className="animate-spin -ml-0.5 mr-2 h-3 w-3 text-white" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span>Loading{getLoadingDots()}</span>
          </div>
        </div>
      </div>
      
      {/* Inject required CSS */}
      <style jsx>{`
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        
        .animate-shimmer {
          animation: shimmer 2s infinite;
        }
      `}</style>
    </Container>
  );
};

export default LoadingSpinner;



